import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Button, Modal } from '@spirit-testing/component-library';
import { FileDropzoneComponent } from '../../../components/file-dropzone/file-dropzone.component';
import { ProjectEnvironmentService } from '../../../services/project-environment.service';
import { ResultImportFormat, TestRunService } from '../../../services/test-run.service';
import { ProjectEnvironment } from '../../../models/project-environment.model';
import { ResultImportResult, ResultImportSkippedTest } from '../../../models/result-import.model';
import { Release } from '../../../models/release.model';
import { vorbelegteUmgebung } from '../../../shared/umgebung-name';

const ACCEPTED_EXTENSION = 'xml';
const MAX_FILE_SIZE = 6 * 1024 * 1024;

type Quelle =
  | { readonly id: ResultImportFormat; readonly icon: string; readonly gesperrt: false }
  | { readonly id: 'redmine' | 'csv'; readonly icon: string; readonly gesperrt: true };

const QUELLEN: readonly Quelle[] = [
  { id: 'robot', icon: 'shield', gesperrt: false },
  { id: 'redmine', icon: 'bar_chart', gesperrt: true },
  { id: 'junit', icon: 'image', gesperrt: false },
  { id: 'csv', icon: 'image', gesperrt: true },
];

@Component({
  selector: 'app-testresultat-import-dialog',
  imports: [TranslatePipe, MatIcon, Modal, Button, FileDropzoneComponent],
  templateUrl: './testresultat-import-dialog.component.html',
  styleUrl: './testresultat-import-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestresultatImportDialogComponent {
  private readonly testRunService = inject(TestRunService);
  private readonly environmentService = inject(ProjectEnvironmentService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly visible = model(false);
  readonly release = input<Release | null>(null);
  readonly imported = output<ResultImportResult>();

  protected readonly quellen = QUELLEN;
  protected readonly schritte = [1, 2] as const;
  protected readonly acceptedFileTypes = `.${ACCEPTED_EXTENSION}`;

  protected readonly schritt = signal<1 | 2>(1);
  protected readonly format = signal<ResultImportFormat>('robot');
  protected readonly file = signal<File | null>(null);
  protected readonly busy = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly umgebungen = signal<readonly ProjectEnvironment[]>([]);
  protected readonly umgebungenStatus = signal<'laedt' | 'fehler' | 'geladen'>('laedt');
  protected readonly umgebung = signal<string | null>(null);
  protected readonly bericht = signal<ResultImportResult | null>(null);

  private readonly fertigKnopf = viewChild('fertigKnopf', { read: ElementRef<HTMLElement> });

  protected readonly primaerGesperrt = computed(
    () => this.schritt() === 2 && (this.file() === null || this.busy() || this.umgebungenStatus() !== 'geladen'),
  );

  protected readonly primaerText = computed(() => {
    if (this.schritt() === 1) return 'testResults.upload.next';
    return this.busy() ? 'testResults.upload.importing' : 'testResults.upload.start';
  });

  // Ein abgebrochener Import läuft auf dem Server weiter: sein Erfolg zählt noch für die Seite,
  // aber weder sein Fehler noch sein Ende dürfen in den inzwischen neu geöffneten Dialog greifen.
  private importVersuch = 0;

  constructor() {
    effect(() => {
      if (this.visible()) {
        untracked(() => this.ladeUmgebungen());
      }
    });
    // Der gedrückte Startknopf verschwindet mit dem Bericht; der Fokus bliebe sonst nirgends im Dialog.
    effect(() => this.fertigKnopf()?.nativeElement.focus());
  }

  protected ladeUmgebungen(): void {
    this.umgebungenStatus.set('laedt');
    this.environmentService
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (umgebungen) => {
          this.umgebungen.set(umgebungen);
          this.umgebung.set(vorbelegteUmgebung(umgebungen)?.name ?? null);
          this.umgebungenStatus.set('geladen');
        },
        error: () => this.umgebungenStatus.set('fehler'),
      });
  }

  protected onFileSelected(file: File): void {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (extension !== ACCEPTED_EXTENSION) {
      this.dateiAblehnen('testResults.upload.errors.fileType');
    } else if (file.size > MAX_FILE_SIZE) {
      this.dateiAblehnen('testResults.upload.errors.fileTooLarge');
    } else {
      this.errorMessage.set(null);
      this.file.set(file);
    }
  }

  protected weiterOderStarten(): void {
    if (this.schritt() === 1) {
      this.schritt.set(2);
    } else if (!this.primaerGesperrt()) {
      this.starten(this.file()!);
    }
  }

  protected schliessen(): void {
    this.visible.set(false);
    this.importVersuch++;
    this.schritt.set(1);
    this.format.set('robot');
    this.file.set(null);
    this.busy.set(false);
    this.errorMessage.set(null);
    this.bericht.set(null);
  }

  protected grund(test: ResultImportSkippedTest): string | null {
    if (test.code) {
      const key = `testResults.upload.skipReasons.${test.code}`;
      const text: string = this.translate.instant(key, test.params ?? {});
      if (text !== key && !text.includes('{{')) return text;
    }
    return test.reason || null;
  }

  private starten(file: File): void {
    const versuch = ++this.importVersuch;
    this.busy.set(true);
    this.errorMessage.set(null);
    this.testRunService
      .importTestResults(this.projectId(), this.format(), file, this.umgebung(), this.release()?.id ?? null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ergebnis) => {
          if (versuch === this.importVersuch) this.abschliessen(ergebnis);
          this.imported.emit(ergebnis);
        },
        error: (err: HttpErrorResponse) => {
          if (versuch !== this.importVersuch) return;
          this.errorMessage.set(this.messageFor(err));
          this.busy.set(false);
        },
      });
  }

  // Die Gründe je Test stehen nirgends sonst; darum bleibt der Dialog nur dann nicht offen, wenn es keine gibt.
  private abschliessen(ergebnis: ResultImportResult): void {
    if (ergebnis.skipped.length === 0) {
      this.schliessen();
      return;
    }
    this.busy.set(false);
    this.bericht.set(ergebnis);
  }

  private dateiAblehnen(meldung: string): void {
    this.file.set(null);
    this.errorMessage.set(this.translate.instant(meldung));
  }

  // Der Server liefert bei 400 eine lesbare Meldung ({ error: ... }); nur wenn keine
  // vorliegt (z. B. Netzfehler), auf eine generische Übersetzung zurückfallen.
  private messageFor(err: HttpErrorResponse): string {
    const serverMessage = err?.error?.error;
    return typeof serverMessage === 'string' && serverMessage.length > 0
      ? serverMessage
      : this.translate.instant('testResults.upload.errors.generic');
  }
}
